import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, ContentCopy, Delete, FileDownload, FileUpload } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { ToastContainer } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import { Upload } from "antd";
import { ImageIcon, X } from "lucide-react";
import { useDispatch } from "react-redux";
import CommonPagination from "../../components/Table/CommonPagination";
import CustomTextField from "../../components/FormElements/CustomTextField";
import AlternativeNamesFields from "../../components/FormElements/AlternativeNamesFields";
import {
  alternativeNameOverLimit,
  alternativeNamesFromRecord,
  alternativeNamesPayload,
  emptyAlternativeNames,
} from "../../components/FormElements/alternativeLanguages";
import DateTimeRangePicker from "../../components/FormElements/DateTimeRangePicker";
import Spinner from "../../components/Spinner";
import ConfirmDeleteModal from "../ManageBadge/ConfirmDeleteModal";
import useDebounce from "../ManageUsers/useDebounce";
import { showToast } from "../../store/slices/common";
import routePaths from "../../router/route-paths";
import { uploadImage, getUploadedMediaRecord } from "../../http/uploads";
import {
  createTheme,
  deleteTheme,
  downloadTheme,
  getTheme,
  importTheme,
  listThemes,
  syncThemeDates,
  updateTheme,
} from "../../http/themes";
import { validateFileExtension } from "../../utils";
import {
  createStickerUploadHandler,
  rememberUploadedSticker,
  syncStickerListRef,
} from "../../utils/stickerUploads";
import { usePermissions } from "../../hooks/usePermissions";
import AssignedContentFields, {
  assignedFromTheme,
  MAX_THEME_ASSIGNMENTS,
} from "./AssignedContentFields";
import ThemeChallengeDialog from "./ThemeChallengeDialog";
import SyncDatesDialog from "./SyncDatesDialog";

/** Keep in sync with MAX_THEME_STICKERS in the backend theme validations. */
const MAX_THEME_STICKERS = 30;

const { Dragger } = Upload;
const limit = 10;
const ANNOUNCEMENT_MAX_LENGTH = 500;

const labelStyles = {
  color: "#828282",
  fontSize: "16px",
  position: "relative",
  top: "7px",
};

const isValidMediaId = (id) =>
  typeof id === "string" && /^[0-9a-fA-F]{24}$/.test(id);

const sameIdList = (a, b) =>
  a.length === b.length && a.every((id, index) => String(id) === String(b[index]));

/** Wizard route in a new tab; `?themeId=` tells it to create under the theme. */
const openWizardForTheme = (path, themeId) => {
  const url = `${window.location.origin}${path}?themeId=${encodeURIComponent(themeId)}`;
  window.open(url, "_blank", "noopener");
};

/**
 * Optional decorative accent images rendered on the mobile launchpad as
 * stickers hanging off the cards. `payloadKey` is sent on create/update
 * (media id, or null to clear); `themeField` is the stored URL on the theme.
 */
const ACCENT_IMAGE_CONFIGS = [
  {
    key: "bannerAccent",
    payloadKey: "bannerAccentImageId",
    themeField: "bannerAccentImage",
    label: "Theme card corner",
    hint: "Top-right corner of the theme card",
  },
  {
    key: "challengeAccent",
    payloadKey: "challengeAccentImageId",
    themeField: "challengeAccentImage",
    label: "Between challenge pills",
    hint: "Between the Fashion & Makeup pills",
  },
  {
    key: "karaokeAccent",
    payloadKey: "karaokeAccentImageId",
    themeField: "karaokeAccentImage",
    label: "Karaoke pill corner",
    hint: "Top-right corner of the Karaoke pill",
  },
];

const EMPTY_ACCENT_FILES = {
  bannerAccent: null,
  challengeAccent: null,
  karaokeAccent: null,
};

/**
 * Optional VS-feed vote skin assets. `payloadKey` is sent on create/update
 * (media id, or null to clear); URLs live under theme.voteSkin for preview.
 */
const VOTE_SKIN_CONFIGS = [
  {
    key: "button",
    payloadKey: "voteSkinButtonId",
    themeUrlField: "buttonUrl",
    label: "Vote button",
    kind: "image",
    accept: "image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp",
    maxBytes: 2 * 1024 * 1024,
    maxLabel: "2MB",
    formatsHint: "PNG, JPG, WEBP",
  },
  {
    key: "voteAnimation",
    payloadKey: "voteSkinVoteAnimationId",
    themeUrlField: "voteAnimationUrl",
    label: "Vote animation",
    kind: "lottie",
    accept: ".lottie,.json,application/json",
    maxBytes: 1.5 * 1024 * 1024,
    maxLabel: "1.5MB",
    formatsHint: ".lottie or .json",
    help: "540×960 (9:16), bottom-center origin at vote button, 2.6s, 30fps. App mirrors for the right button.",
  },
  {
    key: "background",
    payloadKey: "voteSkinBackgroundId",
    themeUrlField: "backgroundUrl",
    label: "Background",
    kind: "lottie",
    accept: ".lottie,.json,application/json",
    maxBytes: 3 * 1024 * 1024,
    maxLabel: "3MB",
    formatsHint: ".lottie or .json",
  },
  {
    key: "badge",
    payloadKey: "voteSkinBadgeId",
    themeUrlField: "badgeUrl",
    label: "Theme badge",
    kind: "image",
    accept: "image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp",
    maxBytes: 1 * 1024 * 1024,
    maxLabel: "1MB",
    formatsHint: "PNG, 1024×1024",
    help: "PNG, 1024×1024, with a transparent center so the profile photo shows through. The hole starts 72px from the left and top and is 880px wide. Keep the small icon circle fully opaque so it sits on top of the photo, the same way rank badges do. Do not cut the icon out of the hole.",
  },
  {
    key: "navVsSelected",
    payloadKey: "voteSkinNavVsSelectedId",
    themeUrlField: "navVsSelectedUrl",
    label: "Nav — VS selected",
    kind: "svg",
    accept: "image/svg+xml,.svg",
    maxBytes: 256 * 1024,
    maxLabel: "256KB",
    formatsHint: "SVG",
    help: "Selected-state VS tab icon. Bake fills into the SVG (app loads it as-is).",
  },
  {
    key: "streakShape",
    payloadKey: "voteSkinStreakShapeId",
    themeUrlField: "streakShapeUrl",
    label: "Streak shape",
    kind: "svg",
    accept: "image/svg+xml,.svg",
    maxBytes: 256 * 1024,
    maxLabel: "256KB",
    formatsHint: "SVG",
    help: "Silhouette for the VS streak marks (replaces the flame). Fills are stripped and repainted with the streak gradient below, so only the outline matters.",
  },
];

const EMPTY_VOTE_SKIN_FILES = {
  button: null,
  voteAnimation: null,
  background: null,
  badge: null,
  navVsSelected: null,
  streakShape: null,
};

const EMPTY_VOTE_SKIN_COLORS = {
  homeFeedLightColor: "",
  homeFeedDarkColor: "",
  navAccentColor: "",
  envelopeColor: "",
  streakGradientStartColor: "",
  streakGradientEndColor: "",
};

/** Suggested defaults shown in the form — must match what we persist on create. */
const DEFAULT_VOTE_SKIN_COLORS = {
  homeFeedLightColor: "#EAF1F7",
  homeFeedDarkColor: "#101C34",
  navAccentColor: "#5B9BD8",
};

const isHexColor = (value) =>
  /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(String(value || "").trim());

/** Expand #RGB → #RRGGBB and uppercase. */
const normalizeHexColor = (value) => {
  const hex = String(value || "").trim();
  if (!isHexColor(hex)) return "";
  if (hex.length === 4) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toUpperCase();
  }
  return hex.toUpperCase();
};

const themeHasVoteSkinFiles = (theme) => {
  const skin = theme?.voteSkin;
  return Boolean(
    skin?.buttonUrl ||
      skin?.voteAnimationUrl ||
      skin?.backgroundUrl ||
      skin?.badgeUrl ||
      skin?.navVsSelectedUrl ||
      skin?.streakShapeUrl
  );
};

/** Chip / preview: any filled vote-skin slot counts (packs may be partial). */
const hasCompleteVoteSkin = (theme) => {
  const skin = theme?.voteSkin;
  return Boolean(
    skin?.buttonUrl ||
      skin?.voteAnimationUrl ||
      skin?.backgroundUrl ||
      skin?.badgeUrl ||
      skin?.navVsSelectedUrl ||
      skin?.streakShapeUrl ||
      isHexColor(skin?.homeFeedLightColor) ||
      isHexColor(skin?.homeFeedDarkColor) ||
      isHexColor(skin?.navAccentColor) ||
      isHexColor(skin?.envelopeColor) ||
      isHexColor(skin?.streakGradientStartColor) ||
      isHexColor(skin?.streakGradientEndColor)
  );
};

const statusColor = {
  active: "success",
  upcoming: "info",
  ended: "default",
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "90vh",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  button: {
    backgroundColor: "#142C5C !important",
    color: "#fff !important",
    borderRadius: "6px !important",
    fontSize: "14px !important",
    height: "40px !important",
    borderColor: "#142C5C !important",
    "&:hover": {
      backgroundColor: "#0f2247 !important",
    },
  },
  tableContainer: {
    flex: 1,
    overflowY: "auto",
    minHeight: "calc(100vh - 195px)",
    maxHeight: "calc(100vh - 195px)",
    boxShadow: "none !important",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
    display: "flex",
    justifyContent: "flex-end",
    paddingBottom: "10px",
  },
  dialogContent: {
    maxWidth: "100%",
    padding: theme.spacing(2),
  },
}));

export default function ManageTheme() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { isAdmin } = usePermissions();

  const [loading, setLoading] = useState(false);
  const [themes, setThemes] = useState([]);
  const [page, setPage] = useState(0);
  const [totalThemes, setTotalThemes] = useState(0);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search);

  const [openDialog, setOpenDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);

  const [themeTitle, setThemeTitle] = useState("");
  const [alternativeNames, setAlternativeNames] = useState(emptyAlternativeNames);
  // Feed post copy (@pagent announcement). Blank → backend default line.
  const [announcement, setAnnouncement] = useState("");
  const [themeStartDate, setThemeStartDate] = useState(null);
  const [themeEndDate, setThemeEndDate] = useState(null);
  const [bannerFileData, setBannerFileData] = useState(null);
  // Keyed by ACCENT_IMAGE_CONFIGS key → { url, _id } | null
  const [accentFiles, setAccentFiles] = useState(EMPTY_ACCENT_FILES);
  // Library can hold more than the three accents the app displays.
  const [libraryStickers, setLibraryStickers] = useState([]);
  const libraryStickersRef = useRef([]);
  syncStickerListRef(libraryStickersRef, libraryStickers);
  const libraryUploadRef = useRef(null);
  if (!libraryUploadRef.current) {
    libraryUploadRef.current = createStickerUploadHandler({
      max: MAX_THEME_STICKERS,
      getCurrent: () => libraryStickersRef.current.length,
      folder: "theme-stickers",
      limitMessage: `A theme can store up to ${MAX_THEME_STICKERS} stickers.`,
      onUploaded: (media) => {
        setLibraryStickers(
          rememberUploadedSticker(
            libraryStickersRef,
            media,
            MAX_THEME_STICKERS
          )
        );
      },
      dispatch,
      setLoading,
    });
  }
  const [assignedDances, setAssignedDances] = useState([]);
  const [assignedKaraokes, setAssignedKaraokes] = useState([]);
  const [assignedChallenges, setAssignedChallenges] = useState([]);
  // Assignment ids as loaded into the edit form. Save only sends a list that
  // differs from this, so content linked from another tab (Add song / Add
  // dance wizards, challenge quick-add) is never overwritten by a stale form.
  const initialAssignmentsRef = useRef({
    dances: [],
    karaokes: [],
    challenges: [],
  });
  // Vote skin assets → keyed by VOTE_SKIN_CONFIGS key → { url, _id? } | null
  const [voteSkinFiles, setVoteSkinFiles] = useState(EMPTY_VOTE_SKIN_FILES);
  const [voteSkinColors, setVoteSkinColors] = useState(EMPTY_VOTE_SKIN_COLORS);
  /** Distinct ET voting days required to keep the pack after the week (0 = free). */
  const [voteSkinKeepDaysRequired, setVoteSkinKeepDaysRequired] = useState(5);

  const [challengeDialogOpen, setChallengeDialogOpen] = useState(false);
  const [syncPrompt, setSyncPrompt] = useState(null);
  const [syncingDates, setSyncingDates] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [themeToDelete, setThemeToDelete] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const bundleInputRef = useRef(null);

  useEffect(() => {
    fetchThemes();
  }, [page, debouncedSearch]);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const response = await listThemes({
        page: page + 1,
        limit,
        search: debouncedSearch || undefined,
      });
      const list = response?.data?.list || [];
      setThemes(list);
      setTotalThemes(response?.data?.resultCount || 0);
      return list;
    } catch (error) {
      console.error("Error fetching themes:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to load themes. Please try again.",
        })
      );
      return [];
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setThemeTitle("");
    setAlternativeNames(emptyAlternativeNames());
    setAnnouncement("");
    setThemeStartDate(null);
    setThemeEndDate(null);
    setBannerFileData(null);
    setAccentFiles(EMPTY_ACCENT_FILES);
    setLibraryStickers([]);
    setAssignedDances([]);
    setAssignedKaraokes([]);
    setAssignedChallenges([]);
    setVoteSkinFiles(EMPTY_VOTE_SKIN_FILES);
    setVoteSkinColors(EMPTY_VOTE_SKIN_COLORS);
    setVoteSkinKeepDaysRequired(5);
    setEditingTheme(null);
    setIsEditMode(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    // Color <input type="color"> always shows a swatch — seed defaults into
    // state so Save actually persists the colors the admin sees (empty state
    // used to leave the swatch looking set while the payload omitted them).
    setVoteSkinColors({ ...DEFAULT_VOTE_SKIN_COLORS });
    setVoteSkinKeepDaysRequired(5);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    resetForm();
  };

  const handleOpenEdit = (theme) => {
    setIsEditMode(true);
    setEditingTheme(theme);
    setThemeTitle(theme.title || "");
    setAlternativeNames(alternativeNamesFromRecord(theme));
    setAnnouncement(theme.announcement || "");
    setThemeStartDate(theme.startDate ? dayjs(theme.startDate) : null);
    setThemeEndDate(theme.endDate ? dayjs(theme.endDate) : null);
    setBannerFileData(
      theme.bannerImage
        ? { url: theme.bannerImage, _id: theme.bannerImageId }
        : null
    );
    setAccentFiles(
      ACCENT_IMAGE_CONFIGS.reduce(
        (acc, { key, themeField }) => ({
          ...acc,
          [key]: theme[themeField] ? { url: theme[themeField] } : null,
        }),
        {}
      )
    );
    setLibraryStickers(
      (theme.stickers || [])
        .map((sticker) => {
          const raw =
            sticker?.mediaId?._id || sticker?.mediaId || sticker?._id || "";
          return {
            mediaId: raw ? String(raw) : "",
            url: typeof sticker === "string" ? sticker : sticker?.url || "",
          };
        })
        .filter((sticker) => sticker.url)
    );
    const dances = assignedFromTheme(theme, "dances", "danceIds", "Untitled dance");
    const karaokes = assignedFromTheme(
      theme,
      "karaokes",
      "karaokeIds",
      "Untitled karaoke"
    );
    const challenges = assignedFromTheme(
      theme,
      "challenges",
      "challengeIds",
      "Untitled challenge"
    );
    setAssignedDances(dances);
    setAssignedKaraokes(karaokes);
    setAssignedChallenges(challenges);
    initialAssignmentsRef.current = {
      dances: dances.map((item) => item._id),
      karaokes: karaokes.map((item) => item._id),
      challenges: challenges.map((item) => item._id),
    };
    setVoteSkinFiles(
      VOTE_SKIN_CONFIGS.reduce(
        (acc, { key, themeUrlField }) => ({
          ...acc,
          [key]: theme.voteSkin?.[themeUrlField]
            ? { url: theme.voteSkin[themeUrlField] }
            : null,
        }),
        { ...EMPTY_VOTE_SKIN_FILES }
      )
    );
    setVoteSkinColors({
      homeFeedLightColor:
        normalizeHexColor(theme.voteSkin?.homeFeedLightColor) ||
        (themeHasVoteSkinFiles(theme)
          ? DEFAULT_VOTE_SKIN_COLORS.homeFeedLightColor
          : ""),
      homeFeedDarkColor:
        normalizeHexColor(theme.voteSkin?.homeFeedDarkColor) ||
        (themeHasVoteSkinFiles(theme)
          ? DEFAULT_VOTE_SKIN_COLORS.homeFeedDarkColor
          : ""),
      navAccentColor:
        normalizeHexColor(theme.voteSkin?.navAccentColor) ||
        (themeHasVoteSkinFiles(theme)
          ? DEFAULT_VOTE_SKIN_COLORS.navAccentColor
          : ""),
      envelopeColor: normalizeHexColor(theme.voteSkin?.envelopeColor) || "",
      streakGradientStartColor:
        normalizeHexColor(theme.voteSkin?.streakGradientStartColor) || "",
      streakGradientEndColor:
        normalizeHexColor(theme.voteSkin?.streakGradientEndColor) || "",
    });
    {
      const raw = theme.voteSkin?.keepDaysRequired;
      const n = Number(raw);
      setVoteSkinKeepDaysRequired(
        Number.isFinite(n) && n >= 0 && n <= 7 ? Math.round(n) : 5
      );
    }
    setOpenDialog(true);
  };

  const addLibrarySticker = (media) => {
    const mediaId = media?._id ? String(media._id) : "";
    if (!isValidMediaId(mediaId) || !media?.url) return;
    if (
      libraryStickersRef.current.some((sticker) => sticker.mediaId === mediaId)
    ) {
      return;
    }
    if (libraryStickersRef.current.length >= MAX_THEME_STICKERS) {
      dispatch(
        showToast({
          color: "error",
          msg: `A theme can store up to ${MAX_THEME_STICKERS} stickers.`,
        })
      );
      return;
    }
    const next = [
      ...libraryStickersRef.current,
      { mediaId, url: media.url },
    ];
    libraryStickersRef.current = next;
    setLibraryStickers(next);
  };

  const handleImageFileUpload = async (
    options,
    { label, onUploaded, folder = "themes" }
  ) => {
    const { file, onSuccess, onError } = options;

    const validFormats = ["image/jpeg", "image/jpg", "image/png", "image/jfif"];
    const isValidMimeType = file.type && validFormats.includes(file.type);
    const isValidExtension = validateFileExtension(file.name, "");

    if (!isValidMimeType && !isValidExtension) {
      dispatch(
        showToast({
          color: "error",
          msg: "Invalid file format. Please upload a JPG, JPEG, or PNG image.",
        })
      );
      onError(new Error("Invalid file format"));
      return;
    }

    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      dispatch(
        showToast({
          color: "error",
          msg: "File size exceeds 5MB limit. Please upload a smaller file.",
        })
      );
      onError(new Error("File size too large"));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);
      setLoading(true);
      const res = await uploadImage(formData);
      const media = getUploadedMediaRecord(res);
      onUploaded(media);
      dispatch(
        showToast({
          color: "success",
          msg: `${label} uploaded successfully.`,
        })
      );
      onSuccess(media);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            `Failed to upload ${label.toLowerCase()}. Please try again.`,
        })
      );
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleBannerFileUpload = (options) =>
    handleImageFileUpload(options, {
      label: "Banner image",
      onUploaded: setBannerFileData,
    });

  const makeAccentFileUpload = (key, label) => (options) =>
    handleImageFileUpload(options, {
      label,
      folder: "theme-stickers",
      onUploaded: (media) => {
        setAccentFiles((prev) => ({ ...prev, [key]: media }));
        addLibrarySticker(media);
      },
    });

  const assignAccentFromLibrary = (key, sticker) => {
    if (!isValidMediaId(sticker?.mediaId) || !sticker?.url) return;
    setAccentFiles((prev) => ({
      ...prev,
      [key]: { _id: sticker.mediaId, url: sticker.url },
    }));
  };

  const isVoteSkinImage = (file) => {
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    return (
      (file.type && validTypes.includes(file.type)) ||
      ["jpg", "jpeg", "png", "webp"].includes(ext)
    );
  };

  const isVoteSkinLottie = (file) => {
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    return (
      ext === "lottie" ||
      ext === "json" ||
      file.type === "application/json"
    );
  };

  const isVoteSkinSvg = (file) => {
    const ext = (file.name || "").split(".").pop()?.toLowerCase();
    return ext === "svg" || file.type === "image/svg+xml";
  };

  const voteSkinFormatError = (kind) => {
    if (kind === "image") {
      return "Invalid file format. Please upload a PNG, JPG, or WEBP image.";
    }
    if (kind === "svg") {
      return "Invalid file format. Please upload an .svg file.";
    }
    return "Invalid file format. Please upload a .lottie or .json file.";
  };

  const makeVoteSkinUpload = (config) => async (options) => {
    const { file, onSuccess, onError } = options;
    const { key, label, kind, maxBytes, maxLabel } = config;

    const valid =
      kind === "image"
        ? isVoteSkinImage(file)
        : kind === "svg"
          ? isVoteSkinSvg(file)
          : isVoteSkinLottie(file);

    if (!valid) {
      dispatch(
        showToast({
          color: "error",
          msg: voteSkinFormatError(kind),
        })
      );
      onError(new Error("Invalid file format"));
      return;
    }

    if (file.size > maxBytes) {
      dispatch(
        showToast({
          color: "error",
          msg: `File size exceeds ${maxLabel} limit. Please upload a smaller file.`,
        })
      );
      onError(new Error("File size too large"));
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", "themes");
      setLoading(true);
      const res = await uploadImage(formData);
      const media = getUploadedMediaRecord(res);
      setVoteSkinFiles((prev) => ({ ...prev, [key]: media }));
      dispatch(
        showToast({
          color: "success",
          msg: `${label} uploaded successfully.`,
        })
      );
      onSuccess(media);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            `Failed to upload ${label.toLowerCase()}. Please try again.`,
        })
      );
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    if (!themeTitle.trim()) {
      dispatch(showToast({ color: "error", msg: "Theme title is required." }));
      return false;
    }
    if (themeTitle.trim().length > 80) {
      dispatch(
        showToast({
          color: "error",
          msg: "Theme title must not exceed 80 characters.",
        })
      );
      return false;
    }
    const longAlternative = alternativeNameOverLimit(alternativeNames, 80);
    if (longAlternative) {
      dispatch(
        showToast({
          color: "error",
          msg: `${longAlternative.label} name must not exceed 80 characters.`,
        })
      );
      return false;
    }
    if (announcement.trim().length > ANNOUNCEMENT_MAX_LENGTH) {
      dispatch(
        showToast({
          color: "error",
          msg: `Announcement must not exceed ${ANNOUNCEMENT_MAX_LENGTH} characters.`,
        })
      );
      return false;
    }
    if (!themeStartDate || !themeEndDate) {
      dispatch(
        showToast({ color: "error", msg: "Start and end dates are required." })
      );
      return false;
    }
    if (dayjs(themeEndDate).isBefore(dayjs(themeStartDate))) {
      dispatch(
        showToast({
          color: "error",
          msg: "End date must be after start date.",
        })
      );
      return false;
    }
    if (!isEditMode && !isValidMediaId(bannerFileData?._id)) {
      dispatch(
        showToast({ color: "error", msg: "Banner image is required." })
      );
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      const payload = {
        title: themeTitle.trim(),
        localizedTitles: alternativeNamesPayload(alternativeNames),
        announcement: announcement.trim(),
        startDate: dayjs(themeStartDate).toISOString(),
        endDate: dayjs(themeEndDate).toISOString(),
      };

      if (isValidMediaId(bannerFileData?._id)) {
        payload.bannerImageId = bannerFileData._id;
      }

      ACCENT_IMAGE_CONFIGS.forEach(({ key, payloadKey, themeField }) => {
        const fileData = accentFiles[key];
        if (isValidMediaId(fileData?._id)) {
          // Newly uploaded accent image
          payload[payloadKey] = fileData._id;
        } else if (isEditMode && editingTheme?.[themeField] && !fileData?.url) {
          // Existing accent removed — explicit null clears it server-side
          payload[payloadKey] = null;
        }
      });

      const libraryIds = libraryStickers.map((sticker) => sticker.mediaId);
      if (libraryIds.some((id) => !isValidMediaId(id))) {
        dispatch(
          showToast({
            color: "error",
            msg: "Some library stickers can't be saved. Remove them and upload again.",
          })
        );
        setLoading(false);
        return;
      }
      payload.stickerIds = libraryIds;
      // Edit: only id lists the admin changed in this form are sent; the
      // server leaves omitted ones alone (see initialAssignmentsRef).
      const assignmentPayload = [
        ["danceIds", "dances", assignedDances],
        ["karaokeIds", "karaokes", assignedKaraokes],
        ["challengeIds", "challenges", assignedChallenges],
      ];
      for (const [payloadKey, initialKey, items] of assignmentPayload) {
        const ids = items.map((item) => item._id);
        if (!isEditMode || !sameIdList(ids, initialAssignmentsRef.current[initialKey])) {
          payload[payloadKey] = ids;
        }
      }

      VOTE_SKIN_CONFIGS.forEach(({ key, payloadKey, themeUrlField }) => {
        const fileData = voteSkinFiles[key];
        if (isValidMediaId(fileData?._id)) {
          // Newly uploaded vote-skin asset
          payload[payloadKey] = fileData._id;
        } else if (
          isEditMode &&
          editingTheme?.voteSkin?.[themeUrlField] &&
          !fileData?.url
        ) {
          // Existing asset removed — explicit null clears it server-side
          payload[payloadKey] = null;
        }
      });

      const colorPayloads = [
        {
          stateKey: "homeFeedLightColor",
          payloadKey: "voteSkinHomeFeedLightColor",
          label: "Home feed light color",
          existing: editingTheme?.voteSkin?.homeFeedLightColor,
        },
        {
          stateKey: "homeFeedDarkColor",
          payloadKey: "voteSkinHomeFeedDarkColor",
          label: "Home feed dark color",
          existing: editingTheme?.voteSkin?.homeFeedDarkColor,
        },
        {
          stateKey: "navAccentColor",
          payloadKey: "voteSkinNavAccentColor",
          label: "Nav accent color",
          existing: editingTheme?.voteSkin?.navAccentColor,
        },
        {
          stateKey: "envelopeColor",
          payloadKey: "voteSkinEnvelopeColor",
          label: "Envelope color",
          existing: editingTheme?.voteSkin?.envelopeColor,
        },
        {
          stateKey: "streakGradientStartColor",
          payloadKey: "voteSkinStreakGradientStartColor",
          label: "Streak gradient start color",
          existing: editingTheme?.voteSkin?.streakGradientStartColor,
        },
        {
          stateKey: "streakGradientEndColor",
          payloadKey: "voteSkinStreakGradientEndColor",
          label: "Streak gradient end color",
          existing: editingTheme?.voteSkin?.streakGradientEndColor,
        },
      ];
      const hasVoteSkinFileInForm = VOTE_SKIN_CONFIGS.some(({ key }) => {
        const fileData = voteSkinFiles[key];
        return isValidMediaId(fileData?._id) || Boolean(fileData?.url);
      });

      for (const { stateKey, payloadKey, label, existing } of colorPayloads) {
        const raw = String(voteSkinColors[stateKey] || "").trim();
        if (raw && !isHexColor(raw)) {
          dispatch(
            showToast({
              color: "error",
              msg: `${label} must be #RGB or #RRGGBB.`,
            })
          );
          setLoading(false);
          return;
        }
        // Prefer explicit form value; if pack files exist but the field was left
        // looking "set" via the color swatch defaults, persist those defaults.
        const hex =
          normalizeHexColor(raw) ||
          (hasVoteSkinFileInForm ? DEFAULT_VOTE_SKIN_COLORS[stateKey] : "");
        if (hex) {
          payload[payloadKey] = hex;
        } else if (isEditMode && existing) {
          payload[payloadKey] = null;
        }
      }

      {
        const days = Number(voteSkinKeepDaysRequired);
        if (!Number.isFinite(days) || days < 0 || days > 7) {
          dispatch(
            showToast({
              color: "error",
              msg: "Days of voting required to keep must be an integer from 0 to 7.",
            })
          );
          setLoading(false);
          return;
        }
        payload.voteSkinKeepDaysRequired = Math.round(days);
      }

      const datesChanged =
        isEditMode &&
        editingTheme &&
        (!dayjs(editingTheme.startDate).isSame(dayjs(themeStartDate)) ||
          !dayjs(editingTheme.endDate).isSame(dayjs(themeEndDate)));
      const linkedChallenges = assignedChallenges;
      const linkedSongs = assignedKaraokes;
      const linkedDances = assignedDances;

      if (isEditMode && editingTheme?._id) {
        await updateTheme(editingTheme._id, payload);
        dispatch(
          showToast({ color: "success", msg: "Theme updated successfully." })
        );
      } else {
        await createTheme(payload);
        dispatch(
          showToast({ color: "success", msg: "Theme created successfully." })
        );
      }

      if (
        datesChanged &&
        (linkedChallenges.length > 0 ||
          linkedSongs.length > 0 ||
          linkedDances.length > 0)
      ) {
        setSyncPrompt({
          themeId: editingTheme._id,
          challengeCount: linkedChallenges.length,
          songCount: linkedSongs.length,
          danceCount: linkedDances.length,
        });
      }

      handleCloseDialog();
      fetchThemes();
    } catch (error) {
      console.error("Error saving theme:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to save theme. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // On a saved theme the server already linked the challenge (themeId on
  // create); the local list just mirrors it so Save keeps it. On an unsaved
  // theme the link is written with the theme.
  const handleChallengeCreated = (created) => {
    const createdId = String(created._id);
    setAssignedChallenges((prev) =>
      [
        ...prev.filter((item) => item._id !== createdId),
        {
          _id: createdId,
          title: created.title || "Untitled challenge",
          artist: "",
        },
      ].slice(0, MAX_THEME_ASSIGNMENTS)
    );
    if (created.themeId) {
      // Already linked server-side: part of the baseline, not a pending edit.
      const baseline = initialAssignmentsRef.current.challenges;
      if (!baseline.includes(createdId)) {
        initialAssignmentsRef.current = {
          ...initialAssignmentsRef.current,
          challenges: [...baseline, createdId],
        };
      }
    }
    dispatch(
      showToast({
        color: "success",
        msg: created.themeId
          ? "Challenge added to this theme."
          : "Challenge created. It links to the theme when you save.",
      })
    );
  };

  const handleAddChallenge = () => {
    if (assignedChallenges.length >= MAX_THEME_ASSIGNMENTS) {
      dispatch(
        showToast({
          color: "error",
          msg: `A theme can link up to ${MAX_THEME_ASSIGNMENTS} challenges.`,
        })
      );
      return;
    }
    setChallengeDialogOpen(true);
  };

  // Songs and dances need the audio wizards, which open in a new tab with the
  // theme id. The server links the track on create; the focus listener below
  // pulls it into this form when the admin comes back.
  const openTrackWizard = (path, kindLabel, assigned) => {
    if (!isEditMode || !editingTheme?._id) return;
    if (assigned.length >= MAX_THEME_ASSIGNMENTS) {
      dispatch(
        showToast({
          color: "error",
          msg: `A theme can link up to ${MAX_THEME_ASSIGNMENTS} ${kindLabel}.`,
        })
      );
      return;
    }
    const savedStart = editingTheme.startDate && dayjs(editingTheme.startDate);
    const savedEnd = editingTheme.endDate && dayjs(editingTheme.endDate);
    const datesUnsaved =
      !savedStart ||
      !savedEnd ||
      !savedStart.isSame(dayjs(themeStartDate)) ||
      !savedEnd.isSame(dayjs(themeEndDate));
    if (datesUnsaved) {
      dispatch(
        showToast({
          color: "warning",
          msg: "Save the theme first so the new track uses the updated dates.",
        })
      );
      return;
    }
    openWizardForTheme(path, editingTheme._id);
  };

  const handleAddSong = () =>
    openTrackWizard(routePaths.uploadKaraoke, "songs", assignedKaraokes);
  const handleAddDance = () =>
    openTrackWizard(routePaths.uploadDance, "dances", assignedDances);

  // When the admin returns from a wizard tab, merge anything linked to this
  // theme since the form opened. Local removals are kept: only ids that were
  // not in the baseline are added.
  useEffect(() => {
    if (!openDialog || !isEditMode || !editingTheme?._id) return undefined;
    const themeId = editingTheme._id;
    let active = true;
    const refresh = async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const response = await getTheme(themeId);
        const theme = response?.data;
        if (!active || !theme?._id) return;
        const kinds = [
          ["dances", "danceIds", "Untitled dance", setAssignedDances],
          ["karaokes", "karaokeIds", "Untitled karaoke", setAssignedKaraokes],
          ["challenges", "challengeIds", "Untitled challenge", setAssignedChallenges],
        ];
        const nextBaseline = { ...initialAssignmentsRef.current };
        for (const [docsKey, idsKey, fallback, setter] of kinds) {
          const server = assignedFromTheme(theme, docsKey, idsKey, fallback);
          const baseline = nextBaseline[docsKey];
          const added = server.filter((item) => !baseline.includes(item._id));
          if (!added.length) continue;
          setter((prev) => {
            const have = new Set(prev.map((item) => item._id));
            return [...prev, ...added.filter((item) => !have.has(item._id))].slice(
              0,
              MAX_THEME_ASSIGNMENTS
            );
          });
          nextBaseline[docsKey] = [...baseline, ...added.map((item) => item._id)];
        }
        initialAssignmentsRef.current = nextBaseline;
      } catch (error) {
        console.error(error);
      }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      active = false;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [openDialog, isEditMode, editingTheme?._id]);

  const handleSyncDates = async () => {
    if (!syncPrompt) return;
    setSyncingDates(true);
    try {
      const response = await syncThemeDates(syncPrompt.themeId);
      const challenges = response?.data?.challenges ?? 0;
      const karaokes = response?.data?.karaokes ?? 0;
      const dances = response?.data?.dances ?? 0;
      const parts = [];
      if (challenges) {
        parts.push(`${challenges} challenge${challenges === 1 ? "" : "s"}`);
      }
      if (karaokes) parts.push(`${karaokes} song${karaokes === 1 ? "" : "s"}`);
      if (dances) parts.push(`${dances} dance${dances === 1 ? "" : "s"}`);
      dispatch(
        showToast({
          color: "success",
          msg: parts.length
            ? `Updated ${parts.join(" and ")} to the new theme dates.`
            : "Linked content already matched the theme dates.",
        })
      );
      setSyncPrompt(null);
    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Could not update the linked content. Please try again.",
        })
      );
    } finally {
      setSyncingDates(false);
    }
  };

  const handleDeleteClick = (theme) => {
    setThemeToDelete(theme);
    setOpenDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!themeToDelete?._id) return;
    try {
      setLoading(true);
      await deleteTheme(themeToDelete._id);
      dispatch(
        showToast({ color: "success", msg: "Theme deleted successfully." })
      );
      setOpenDeleteModal(false);
      setThemeToDelete(null);
      if (openDialog) handleCloseDialog();
      fetchThemes();
    } catch (error) {
      console.error("Error deleting theme:", error);
      dispatch(
        showToast({
          color: "error",
          msg:
            error.response?.data?.message ||
            "Failed to delete theme. Please try again.",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  const copyDeepLink = async (link, { silent = false } = {}) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      if (!silent) {
        dispatch(showToast({ color: "success", msg: "Deep link copied." }));
      }
    } catch (error) {
      console.error("Clipboard write failed:", error);
      dispatch(
        showToast({
          color: "error",
          msg: `Could not copy. Link: ${link}`,
        })
      );
    }
  };

  const handleDownload = async (theme) => {
    try {
      setDownloadingId(theme._id);
      const response = await downloadTheme(theme._id);
      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      const header = response.headers?.["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(header);
      link.href = blobUrl;
      link.download = match?.[1] || `${theme.title || "theme"}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      let message = "Could not download this theme.";
      const data = error?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch (parseError) {
          console.error("Could not read theme download error:", parseError);
        }
      }
      dispatch(showToast({ color: "error", msg: message }));
    } finally {
      setDownloadingId(null);
    }
  };

  const handleBundleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      dispatch(showToast({ color: "error", msg: "Choose a theme .zip file." }));
      return;
    }
    try {
      setBundleBusy(true);
      await importTheme(file);
      dispatch(showToast({ color: "success", msg: "Theme uploaded." }));
      fetchThemes();
    } catch (error) {
      console.error("Error uploading theme:", error);
    } finally {
      setBundleBusy(false);
    }
  };

  const handlePageChange = (_event, newPage) => {
    setPage(newPage);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const bannerPreviewUrl = bannerFileData?.url || null;

  return (
    <PageLayout
      title="Themes"
      loading={loading}
      actions={
        <Box sx={{ display: "flex", gap: 1 }}>
          <input
            ref={bundleInputRef}
            type="file"
            accept=".zip,application/zip"
            hidden
            onChange={handleBundleFile}
          />
          <Button
            className={classes.button}
            startIcon={<FileUpload />}
            disabled={bundleBusy}
            onClick={() => bundleInputRef.current?.click()}
          >
            {bundleBusy ? "Uploading..." : "Upload theme"}
          </Button>
          <Button
            className={classes.button}
            startIcon={<Add />}
            onClick={handleOpenCreate}
          >
            Schedule Theme
          </Button>
        </Box>
      }
      search={
        <input
          placeholder="Search themes..."
          type="text"
          value={search}
          onChange={handleSearchChange}
          className="search-field-ui"
          style={{ height: "40px", width: "100%" }}
        />
      }
    >
      <ToastContainer />

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Banner</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Owners</TableCell>
              <TableCell>Deep link</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {themes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No themes scheduled yet.
                </TableCell>
              </TableRow>
            ) : (
              themes.map((theme) => (
                <TableRow key={theme._id} hover>
                  <TableCell>
                    {theme.bannerImage ? (
                      <Box
                        component="img"
                        src={theme.bannerImage}
                        alt={theme.title}
                        sx={{
                          width: 72,
                          height: 48,
                          objectFit: "cover",
                          borderRadius: "6px",
                        }}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {theme.title}
                      {hasCompleteVoteSkin(theme) && (
                        <Chip
                          size="small"
                          label="Vote skin"
                          variant="outlined"
                          sx={{ height: 18, fontSize: "10px" }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {theme.startDate
                      ? dayjs(theme.startDate).format("MMM D, YYYY h:mm A")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {theme.endDate
                      ? dayjs(theme.endDate).format("MMM D, YYYY h:mm A")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={theme.status || "—"}
                      color={statusColor[theme.status] || "default"}
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {theme.ownerCount == null
                      ? "—"
                      : theme.ownerCount.toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {theme.postDeepLink ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={theme.postId ? "In feed" : "Link ready"}
                          color={theme.postId ? "success" : "info"}
                        />
                        <Tooltip title="Copy Branch deep link">
                          <IconButton
                            size="small"
                            onClick={() => copyDeepLink(theme.postDeepLink)}
                            aria-label="Copy deep link"
                          >
                            <ContentCopy fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Tooltip title="The link is created when the theme is saved. Refresh if it is still missing.">
                        <Chip size="small" label="Link pending" variant="outlined" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      startIcon={<FileDownload />}
                      disabled={downloadingId === theme._id}
                      onClick={() => handleDownload(theme)}
                      sx={{ textTransform: "none", mr: 1 }}
                    >
                      {downloadingId === theme._id ? "Downloading..." : "Download"}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => handleOpenEdit(theme)}
                      sx={{ textTransform: "none", mr: 1 }}
                    >
                      Edit
                    </Button>
                    {isAdmin && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(theme)}
                        sx={{ textTransform: "none" }}
                      >
                        Delete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className={classes.paginationContainer}>
        <CommonPagination
          rowsPerPage={limit}
          count={totalThemes}
          page={page}
          onPageChange={handlePageChange}
        />
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullWidth
        sx={{
          zIndex: 9,
          "& .MuiDialog-container .MuiPaper-elevation": {
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, textAlign: "center", pt: 3 }}>
          {isEditMode ? "Edit Theme" : "Schedule Theme of the Week"}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box mt={1}>
            <CustomTextField
              label="Theme title"
              value={themeTitle}
              onChange={(e) => setThemeTitle(e.target.value)}
              inputProps={{ maxLength: 80 }}
            />
            <Typography variant="caption" sx={{ color: "#555", display: "block", mt: 0.5 }}>
              Default English title. Shown when a language has no alternative name.
            </Typography>
            <AlternativeNamesFields
              key={editingTheme?._id || "new"}
              value={alternativeNames}
              onChange={setAlternativeNames}
              maxLength={80}
              onEnglishTitleChange={setThemeTitle}
            />
          </Box>

          <Box mt={2}>
            <CustomTextField
              label="Feed post announcement (optional)"
              value={announcement}
              onChange={(e) => setAnnouncement(e.target.value)}
              multiline
              rows={3}
              inputProps={{ maxLength: ANNOUNCEMENT_MAX_LENGTH }}
            />
            <Typography
              variant="caption"
              sx={{ color: "#555", display: "block", mt: 0.5 }}
            >
              Posted from @pagent to the home feed only while this theme is
              live. The deep link is ready to copy as soon as the theme is
              saved, including before it goes live. The banner image is the
              logo. Blank uses a default line.{" "}
              {announcement.length}/{ANNOUNCEMENT_MAX_LENGTH}
            </Typography>
            {isEditMode && editingTheme?.postDeepLink && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                  p: 1,
                  borderRadius: "6px",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    fontFamily: "monospace",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {editingTheme.postDeepLink}
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopy fontSize="small" />}
                  onClick={() => copyDeepLink(editingTheme.postDeepLink)}
                  sx={{ textTransform: "none", fontSize: "12px" }}
                >
                  Copy link
                </Button>
              </Box>
            )}
          </Box>

          <Box mt={2}>
            <DateTimeRangePicker
              startDate={themeStartDate}
              endDate={themeEndDate}
              onStartDateChange={setThemeStartDate}
              onEndDateChange={setThemeEndDate}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
              <Typography
                variant="caption"
                sx={{ color: "#555", fontWeight: 600 }}
              >
                Duration:
              </Typography>
              {[
                { label: "1 Week", days: 7 },
                { label: "2 Weeks", days: 14 },
              ].map((preset) => (
                <Button
                  key={preset.days}
                  size="small"
                  variant="outlined"
                  disabled={!themeStartDate}
                  onClick={() => {
                    const start = dayjs(themeStartDate);
                    if (start.isValid()) {
                      setThemeEndDate(start.add(preset.days, "day"));
                    }
                  }}
                  sx={{
                    minWidth: 0,
                    px: 1.5,
                    py: 0.25,
                    fontSize: "12px",
                    textTransform: "none",
                    borderColor: "#bbb",
                    color: "#333",
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </Box>
          </Box>

          <Box mt={2} mb={1}>
            <label style={{ ...labelStyles, top: "-9px" }}>Banner Image</label>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              First image on the theme. Used as the logo on the feed card, the theme modal, and the deep link preview. Square works best.
            </Typography>
            {bannerPreviewUrl ? (
              <Box className="contest-upload-preview contest-upload-preview--wide">
                <img src={bannerPreviewUrl} alt="theme banner" />
                <button
                  className="contest-upload-remove"
                  onClick={() => setBannerFileData(null)}
                  type="button"
                >
                  <X size={14} />
                </button>
              </Box>
            ) : (
              <Dragger
                showUploadList={false}
                customRequest={handleBannerFileUpload}
                accept="image/*"
                className="contest-upload-dragger"
              >
                <Box className="contest-upload-dragger-content">
                  <ImageIcon size={32} strokeWidth={1.5} color="#667eea" />
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Drag & drop banner image here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    or click to browse (JPG, PNG, max 5MB)
                  </Typography>
                </Box>
              </Dragger>
            )}
            {bannerPreviewUrl && (
              <Box sx={{ mt: 1.5 }}>
                <Dragger
                  showUploadList={false}
                  customRequest={handleBannerFileUpload}
                  accept="image/*"
                  className="contest-upload-dragger"
                >
                  <Box className="contest-upload-dragger-content">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Replace banner image
                    </Typography>
                  </Box>
                </Dragger>
              </Box>
            )}
          </Box>

          <Box mt={2} mb={1}>
            <label style={{ ...labelStyles, top: "-9px" }}>
              Sticker library (up to {MAX_THEME_STICKERS})
            </label>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Store more than the three stickers the app shows. The launchpad
              still uses only the three accent images below. Challenges,
              dances, and karaoke can reuse these without uploading again.
            </Typography>
            {libraryStickers.length > 0 && (
              <Box className="sticker-preview-row" sx={{ mb: 1.5 }}>
                {libraryStickers.map((sticker) => (
                  <Box
                    key={sticker.mediaId || sticker.url}
                    className="contest-upload-preview contest-upload-preview--sticker"
                  >
                    <img src={sticker.url} alt="library sticker" />
                    <button
                      className="contest-upload-remove"
                      onClick={() =>
                        setLibraryStickers((prev) =>
                          prev.filter((item) => item.mediaId !== sticker.mediaId)
                        )
                      }
                      type="button"
                    >
                      <X size={12} />
                    </button>
                  </Box>
                ))}
              </Box>
            )}
            {libraryStickers.length < MAX_THEME_STICKERS && (
              <Dragger
                multiple
                showUploadList={false}
                customRequest={libraryUploadRef.current}
                accept="image/*"
                className="contest-upload-dragger"
              >
                <Box className="contest-upload-dragger-content">
                  <ImageIcon size={28} strokeWidth={1.5} color="#667eea" />
                  <Typography variant="body2" sx={{ mt: 1, fontWeight: 500 }}>
                    Drag & drop stickers here
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    or click to browse ({libraryStickers.length} of{" "}
                    {MAX_THEME_STICKERS}, PNG/JPG, max 5MB)
                  </Typography>
                </Box>
              </Dragger>
            )}
          </Box>

          <Box mt={2} mb={1}>
            <label style={{ ...labelStyles, top: "-9px" }}>
              Accent Images (optional)
            </label>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              These three are the only stickers shown in the app. Pick one
              from the library or upload a new image. Transparent PNGs look
              best.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              {ACCENT_IMAGE_CONFIGS.map(({ key, label, hint }) => {
                const fileData = accentFiles[key];
                return (
                  <Box key={key} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
                    >
                      {label}
                    </Typography>
                    {fileData?.url ? (
                      <Box className="contest-upload-preview contest-upload-preview--sticker">
                        <img src={fileData.url} alt={label} />
                        <button
                          className="contest-upload-remove"
                          onClick={() =>
                            setAccentFiles((prev) => ({
                              ...prev,
                              [key]: null,
                            }))
                          }
                          type="button"
                        >
                          <X size={12} />
                        </button>
                      </Box>
                    ) : (
                      <Dragger
                        showUploadList={false}
                        customRequest={makeAccentFileUpload(key, label)}
                        accept="image/*"
                        className="contest-upload-dragger"
                      >
                        <Box className="contest-upload-dragger-content">
                          <ImageIcon
                            size={20}
                            strokeWidth={1.5}
                            color="#667eea"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 0.5, textAlign: "center" }}
                          >
                            Upload
                          </Typography>
                        </Box>
                      </Dragger>
                    )}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {hint}
                    </Typography>
                    {libraryStickers.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                          overflowX: "auto",
                          mt: 0.75,
                          pb: 0.5,
                        }}
                      >
                        {libraryStickers.map((sticker) => {
                          const selected =
                            fileData?._id === sticker.mediaId ||
                            fileData?.url === sticker.url;
                          return (
                            <Box
                              key={`${key}-${sticker.mediaId || sticker.url}`}
                              component="button"
                              type="button"
                              title="Use this sticker"
                              onClick={() => assignAccentFromLibrary(key, sticker)}
                              sx={{
                                width: 36,
                                height: 36,
                                p: 0.25,
                                flex: "0 0 auto",
                                borderRadius: "6px",
                                border: selected
                                  ? "2px solid #142C5C"
                                  : "1px solid #ddd",
                                background: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              <img
                                src={sticker.url}
                                alt=""
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  display: "block",
                                }}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <AssignedContentFields
            dances={assignedDances}
            karaokes={assignedKaraokes}
            challenges={assignedChallenges}
            onDancesChange={setAssignedDances}
            onKaraokesChange={setAssignedKaraokes}
            onChallengesChange={setAssignedChallenges}
            onAddChallenge={handleAddChallenge}
            onAddSong={handleAddSong}
            onAddDance={handleAddDance}
            canAddTracks={isEditMode && Boolean(editingTheme?._id)}
            datesReady={Boolean(themeStartDate && themeEndDate)}
          />

          <Box mt={2} mb={1}>
            <label style={{ ...labelStyles, top: "-9px" }}>
              Vote skin (optional)
            </label>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1.5 }}
            >
              Fill any subset of slots — the app downloads and applies whatever
              is present (button, animation, background, badge, nav SVGs, home
              colors, nav accent). Empty slots keep the bundled defaults.
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              {VOTE_SKIN_CONFIGS.map((config) => {
                const {
                  key,
                  label,
                  kind,
                  accept,
                  formatsHint,
                  maxLabel,
                  help,
                } = config;
                const fileData = voteSkinFiles[key];
                const previewKind =
                  kind === "image" ? "image" : kind === "svg" ? "svg" : "lottie";
                return (
                  <Box key={key}>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
                    >
                      {label}
                    </Typography>
                    {help && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mb: 0.75 }}
                      >
                        {help}
                      </Typography>
                    )}
                    {fileData?.url ? (
                      previewKind === "image" || previewKind === "svg" ? (
                        <Box className="contest-upload-preview contest-upload-preview--sticker">
                          <img src={fileData.url} alt={label} />
                          <button
                            className="contest-upload-remove"
                            onClick={() =>
                              setVoteSkinFiles((prev) => ({
                                ...prev,
                                [key]: null,
                              }))
                            }
                            type="button"
                          >
                            <X size={12} />
                          </button>
                        </Box>
                      ) : (
                        <Box
                          className="contest-upload-preview contest-upload-preview--sticker"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            px: 1,
                            minWidth: 120,
                            backgroundColor: "#f5f5f5",
                            position: "relative",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 600,
                              textAlign: "center",
                              wordBreak: "break-all",
                            }}
                          >
                            Lottie file
                          </Typography>
                          <button
                            className="contest-upload-remove"
                            onClick={() =>
                              setVoteSkinFiles((prev) => ({
                                ...prev,
                                [key]: null,
                              }))
                            }
                            type="button"
                          >
                            <X size={12} />
                          </button>
                        </Box>
                      )
                    ) : (
                      <Dragger
                        showUploadList={false}
                        customRequest={makeVoteSkinUpload(config)}
                        accept={accept}
                        className="contest-upload-dragger"
                      >
                        <Box className="contest-upload-dragger-content">
                          <ImageIcon
                            size={24}
                            strokeWidth={1.5}
                            color="#667eea"
                          />
                          <Typography
                            variant="body2"
                            sx={{ mt: 1, fontWeight: 500 }}
                          >
                            Drag & drop {label.toLowerCase()} here
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            or click to browse ({formatsHint}, max {maxLabel})
                          </Typography>
                        </Box>
                      </Dragger>
                    )}
                  </Box>
                );
              })}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  alignItems: "flex-end",
                }}
              >
                {[
                  {
                    key: "homeFeedLightColor",
                    label: "Home feed — light",
                    help: "Canvas hex behind the home feed in light mode.",
                    fallback: "#EAF1F7",
                  },
                  {
                    key: "homeFeedDarkColor",
                    label: "Home feed — dark",
                    help: "Canvas hex behind the home feed in dark mode.",
                    fallback: "#101C34",
                  },
                  {
                    key: "navAccentColor",
                    label: "Nav — selected accent",
                    help: "Tint for selected tab icons (Home, Launchpad, Profile, etc.) while this pack is active. Match the accent baked into the nav SVGs.",
                    fallback: "#5B9BD8",
                  },
                  {
                    key: "envelopeColor",
                    label: "Envelope",
                    help: "Messages envelope on Home and Notifications while this pack is selected. Leave blank to keep the standard gold. The flap is lightened and the sides darkened from this hex.",
                    fallback: "#FFB32C",
                  },
                  {
                    key: "streakGradientStartColor",
                    label: "Streak gradient — start",
                    help: "Base of the streak marks on the VS feed (bottom of the shape). Leave blank to keep the standard red.",
                    fallback: "#FF2D2D",
                  },
                  {
                    key: "streakGradientEndColor",
                    label: "Streak gradient — end",
                    help: "Tip of the streak marks on the VS feed (top of the shape). Leave blank to keep the standard neon purple.",
                    fallback: "#BC13FE",
                  },
                ].map(({ key, label, help, fallback }) => (
                  <Box key={key} sx={{ minWidth: 200, flex: "1 1 200px" }}>
                    <Typography
                      variant="caption"
                      sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.75 }}
                    >
                      {help}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <input
                        type="color"
                        value={
                          normalizeHexColor(voteSkinColors[key]) || fallback
                        }
                        onChange={(e) =>
                          setVoteSkinColors((prev) => ({
                            ...prev,
                            [key]: normalizeHexColor(e.target.value) || e.target.value.toUpperCase(),
                          }))
                        }
                        style={{
                          width: 40,
                          height: 36,
                          border: "1px solid #ccc",
                          borderRadius: 4,
                          padding: 0,
                          background: "transparent",
                          cursor: "pointer",
                        }}
                      />
                      <CustomTextField
                        label="#RRGGBB"
                        value={voteSkinColors[key]}
                        onChange={(e) =>
                          setVoteSkinColors((prev) => ({
                            ...prev,
                            [key]: e.target.value,
                          }))
                        }
                        fullWidth
                      />
                    </Box>
                  </Box>
                ))}
                <Box sx={{ minWidth: 220, flex: "1 1 220px" }}>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", fontWeight: 600, mb: 0.5 }}
                  >
                    Days of voting required to keep
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.75 }}
                  >
                    Distinct calendar days (ET) with at least one vote during
                    this theme window. After the week ends, users who miss the
                    goal must pay $4.99 to keep the pack. Use 0 for free keep
                    forever.
                  </Typography>
                  <CustomTextField
                    label="Days (0–7)"
                    type="number"
                    inputProps={{ min: 0, max: 7, step: 1 }}
                    value={voteSkinKeepDaysRequired}
                    onChange={(e) => {
                      const n = Number(e.target.value);
                      if (!Number.isFinite(n)) {
                        setVoteSkinKeepDaysRequired(e.target.value);
                        return;
                      }
                      setVoteSkinKeepDaysRequired(
                        Math.min(7, Math.max(0, Math.round(n)))
                      );
                    }}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            className={classes.actionsContainer}
            sx={{
              justifyContent: isEditMode && isAdmin ? "space-between" : "flex-end",
            }}
          >
            {isEditMode && isAdmin && (
              <Button
                startIcon={<Delete />}
                sx={{
                  border: "1px solid #d32f2f",
                  color: "#d32f2f",
                  borderRadius: "6px",
                  fontSize: "14px",
                  textTransform: "none",
                }}
                onClick={() => {
                  handleCloseDialog();
                  handleDeleteClick(editingTheme);
                }}
              >
                Delete
              </Button>
            )}
            <Box>
              <Button
                onClick={handleCloseDialog}
                sx={{
                  border: "1px solid #888",
                  color: "#888",
                  borderRadius: "6px",
                  fontSize: "14px",
                  marginRight: "15px",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button className={classes.button} onClick={handleSave}>
                {isEditMode ? "Update Theme" : "Schedule Theme"}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <ThemeChallengeDialog
        open={challengeDialogOpen}
        onClose={() => setChallengeDialogOpen(false)}
        themeId={isEditMode ? editingTheme?._id : null}
        themeStartDate={themeStartDate}
        themeEndDate={themeEndDate}
        libraryStickers={libraryStickers}
        onCreated={handleChallengeCreated}
      />
      <SyncDatesDialog
        open={Boolean(syncPrompt)}
        challengeCount={syncPrompt?.challengeCount || 0}
        songCount={syncPrompt?.songCount || 0}
        danceCount={syncPrompt?.danceCount || 0}
        updating={syncingDates}
        onSkip={() => {
          if (!syncingDates) setSyncPrompt(null);
        }}
        onUpdate={handleSyncDates}
      />

      {openDeleteModal && (
        <ConfirmDeleteModal
          theme={true}
          onAccept={handleConfirmDelete}
          onReject={() => {
            setOpenDeleteModal(false);
            setThemeToDelete(null);
          }}
        />
      )}
    </PageLayout>
  );
}
